import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';

interface InternalLink {
  anchorEn: string;
  anchorEl: string;
  pathEn: string;
  pathEl: string;
  descriptionEn?: string;
  descriptionEl?: string;
}

interface RelatedResourcesProps {
  links: InternalLink[];
  titleEn?: string;
  titleEl?: string;
  variant?: 'grid' | 'inline' | 'compact';
  maxLinks?: number;
}

export const INTERNAL_LINKS: Record<string, InternalLink> = {
  // Upcoming features (pages carry an "Upcoming feature" badge until released).
  liveHeatmaps: {
    anchorEn: 'Live Heatmaps',
    anchorEl: 'Live Heatmaps',
    pathEn: '/features/live-heatmaps',
    pathEl: '/features/live-heatmaps',
    descriptionEn: 'Real visitors, explained by AI (upcoming)',
    descriptionEl: 'Real visitors, explained by AI (upcoming)',
  },
  abTestingFeature: {
    anchorEn: 'A/B Testing',
    anchorEl: 'A/B Testing',
    pathEn: '/features/ab-testing',
    pathEl: '/features/ab-testing',
    descriptionEn: 'Test the fixes from your audit (upcoming)',
    descriptionEl: 'Test the fixes from your audit (upcoming)',
  },
  analyticsIntegrations: {
    anchorEn: 'Analytics Integrations',
    anchorEl: 'Analytics Integrations',
    pathEn: '/features/analytics-integrations',
    pathEl: '/features/analytics-integrations',
    descriptionEn: 'Your visitor data inside the audit (upcoming)',
    descriptionEl: 'Your visitor data inside the audit (upcoming)',
  },
  visualMockups: {
    anchorEn: 'Visual Mockups',
    anchorEl: 'Visual Mockups',
    pathEn: '/features/visual-mockups',
    pathEl: '/features/visual-mockups',
    descriptionEn: 'See a suggestion on your own page (upcoming)',
    descriptionEl: 'See a suggestion on your own page (upcoming)',
  },
  aiAgentFeature: {
    anchorEn: 'AI Agent',
    anchorEl: 'AI Agent',
    pathEn: '/features/ai-agent',
    pathEl: '/features/ai-agent',
    descriptionEn: 'A growth analyst that knows your site (upcoming)',
    descriptionEl: 'A growth analyst that knows your site (upcoming)',
  },
  figmaDesignAudit: {
    anchorEn: 'Figma Design Audit',
    anchorEl: 'Figma Design Audit',
    pathEn: '/features/figma-design-audit',
    pathEl: '/features/figma-design-audit',
    descriptionEn: 'CRO review before you build (upcoming)',
    descriptionEl: 'CRO review before you build (upcoming)',
  },
  aiHeatmaps: {
    anchorEn: 'AI Predictive Attention',
    anchorEl: 'AI Predictive Attention',
    pathEn: '/features/ai-heatmaps',
    pathEl: '/el/charaktiristika/ai-heatmaps',
    descriptionEn: 'Predictive eye-tracking powered by AI',
    descriptionEl: 'AI eye-tracking για ανάλυση οπτικής προσοχής',
  },
  neuromarketing: {
    anchorEn: 'Neuromarketing',
    anchorEl: 'Neuromarketing',
    pathEn: '/features/neuromarketing',
    pathEl: '/el/charaktiristika/neuromarketing',
    descriptionEn: 'Psychology-driven conversion optimization',
    descriptionEl: 'Βελτιστοποίηση μετατροπών βάσει ψυχολογίας',
  },
  croTechniques: {
    anchorEn: 'CRO Techniques',
    anchorEl: 'Τεχνικές CRO',
    pathEn: '/features/cro-techniques',
    pathEl: '/el/charaktiristika/cro-techniques',
    descriptionEn: 'Proven methods to boost conversions',
    descriptionEl: 'Δοκιμασμένες μέθοδοι αύξησης μετατροπών',
  },
  aiCaptain: {
    anchorEn: 'AI Captain',
    anchorEl: 'AI Captain',
    pathEn: '/how-it-works',
    pathEl: '/el/charaktiristika/ai-captain',
    descriptionEn: 'AI-powered CRO assistant',
    descriptionEl: 'AI βοηθός CRO ανάλυσης',
  },
  pagespeed: {
    anchorEn: 'PageSpeed Insights',
    anchorEl: 'PageSpeed Insights',
    pathEn: '/solutions/technical-analysis',
    pathEl: '/el/charaktiristika/pagespeed-insights',
    descriptionEn: 'Site speed performance audit',
    descriptionEl: 'Ανάλυση ταχύτητας ιστοσελίδας',
  },
  whatIsCro: {
    anchorEn: 'What is CRO',
    anchorEl: 'Τι είναι το CRO',
    pathEn: '/what-is-cro',
    pathEl: '/el/ti-einai-to-cro',
    descriptionEn: 'Complete guide to Conversion Rate Optimization',
    descriptionEl: 'Πλήρης οδηγός Conversion Rate Optimization',
  },
  whatIsUx: {
    anchorEn: 'What is UX',
    anchorEl: 'Τι είναι το UX',
    pathEn: '/what-is-cro',
    pathEl: '/el/ti-einai-to-ux',
    descriptionEn: 'Understanding User Experience design',
    descriptionEl: 'Κατανόηση του UX Design',
  },
  howItWorks: {
    anchorEn: 'How It Works',
    anchorEl: 'Πώς Λειτουργεί',
    pathEn: '/how-it-works',
    pathEl: '/el/pos-leitourgei',
    descriptionEn: 'See the EyeCaptain process',
    descriptionEl: 'Δες τη διαδικασία EyeCaptain',
  },
  uxAuditGuide: {
    anchorEn: 'UX Audit Guide',
    anchorEl: 'Οδηγός UX Audit',
    pathEn: '/free-audit',
    pathEl: '/el/ux-audit-guide',
    descriptionEn: 'Step-by-step UX audit checklist',
    descriptionEl: 'Βήμα-βήμα checklist UX audit',
  },
  heatmapGuide: {
    anchorEn: 'Heatmap Analysis Guide',
    anchorEl: 'Οδηγός Heatmap Ανάλυσης',
    pathEn: '/heatmap-analysis-guide',
    pathEl: '/el/heatmap-analysis-guide',
    descriptionEn: 'Master heatmap audit for CRO',
    descriptionEl: 'Μάθε heatmap ανάλυση για CRO',
  },
  aiCro: {
    anchorEn: 'AI-powered CRO',
    anchorEl: 'AI CRO',
    pathEn: '/how-it-works',
    pathEl: '/el/ai-cro',
    descriptionEn: 'How AI transforms conversion optimization',
    descriptionEl: 'Πώς το AI μεταμορφώνει τη βελτιστοποίηση',
  },
  abTestingGuide: {
    anchorEn: 'A/B Testing Guide',
    anchorEl: 'Οδηγός A/B Testing',
    pathEn: '/ab-testing-guide',
    pathEl: '/el/ab-testing-guide',
    descriptionEn: 'Run effective A/B tests',
    descriptionEl: 'Αποτελεσματικά A/B tests',
  },
  ecommerceCroGuide: {
    anchorEn: 'E-commerce CRO Guide',
    anchorEl: 'Οδηγός E-commerce CRO',
    pathEn: '/ecommerce-cro-guide',
    pathEl: '/el/ecommerce-cro-guide',
    descriptionEn: 'Boost your online store conversions',
    descriptionEl: 'Αύξησε τις μετατροπές του e-shop σου',
  },
  ecommerceWebsiteGuide: {
    anchorEn: 'E-commerce Website Guide',
    anchorEl: 'Οδηγός E-commerce Website',
    pathEn: '/ecommerce-cro-guide',
    pathEl: '/el/ecommerce-website-guide',
    descriptionEn: 'Build a high-converting e-commerce site',
    descriptionEl: 'Δημιούργησε ένα e-shop που μετατρέπει',
  },
  leadgenWebsiteGuide: {
    anchorEn: 'Lead Gen Website Guide',
    anchorEl: 'Οδηγός Lead Gen Website',
    pathEn: '/solutions/lead-generation',
    pathEl: '/el/leadgen-website-guide',
    descriptionEn: 'Optimize your lead generation site',
    descriptionEl: 'Βελτιστοποίησε το lead gen site σου',
  },
  aiToolsGuide: {
    anchorEn: 'AI Tools Guide',
    anchorEl: 'Οδηγός AI Εργαλείων',
    pathEn: '/blog',
    pathEl: '/el/ai-tools-guide',
    descriptionEn: 'Best AI tools for 2026',
    descriptionEl: 'Τα καλύτερα AI εργαλεία για το 2026',
  },
  aiModelsGuide: {
    anchorEn: 'AI Models Guide',
    anchorEl: 'Οδηγός AI Μοντέλων',
    pathEn: '/blog',
    pathEl: '/el/ai-models-guide',
    descriptionEn: 'Compare top AI models',
    descriptionEl: 'Σύγκρινε τα κορυφαία AI μοντέλα',
  },
  croToolsGuide: {
    anchorEn: 'CRO Tools Guide',
    anchorEl: 'Οδηγός CRO Εργαλείων',
    pathEn: '/what-is-cro',
    pathEl: '/el/cro-tools-guide',
    descriptionEn: 'Top CRO tools compared',
    descriptionEl: 'Τα καλύτερα CRO εργαλεία',
  },
  aiMarketingGuide: {
    anchorEn: 'AI Marketing Guide',
    anchorEl: 'Οδηγός AI Marketing',
    pathEn: '/blog',
    pathEl: '/el/ai-marketing-guide',
    descriptionEn: 'AI strategies for digital marketing',
    descriptionEl: 'AI στρατηγικές digital marketing',
  },
  aiPromptingGuide: {
    anchorEn: 'AI Prompting Guide',
    anchorEl: 'Οδηγός AI Prompting',
    pathEn: '/blog',
    pathEl: '/el/ai-prompting-guide',
    descriptionEn: 'Master AI prompts for better results',
    descriptionEl: 'Μάθε AI prompts για καλύτερα αποτελέσματα',
  },
  freeCroAnalysis: {
    anchorEn: 'Free CRO Analysis',
    anchorEl: 'Δωρεάν CRO Ανάλυση',
    pathEn: '/free-audit',
    pathEl: '/el/dorean-cro-analysi',
    descriptionEn: 'Get a free CRO audit of your site',
    descriptionEl: 'Δωρεάν CRO ανάλυση του site σου',
  },
  freeWebsiteAudit: {
    anchorEn: 'Free CRO Audit',
    anchorEl: 'Δωρεάν CRO Audit',
    pathEn: '/free-audit',
    pathEl: '/free-audit',
    descriptionEn: 'AI-powered website UX audit',
    descriptionEl: 'AI website UX audit',
  },
  ecommerceSolution: {
    anchorEn: 'E-commerce Solutions',
    anchorEl: 'E-commerce Λύσεις',
    pathEn: '/solutions/ecommerce',
    pathEl: '/el/lyseis/ecommerce',
    descriptionEn: 'CRO for online stores',
    descriptionEl: 'CRO για e-shops',
  },
  leadgenSolution: {
    anchorEn: 'Lead Generation Solutions',
    anchorEl: 'Λύσεις Lead Generation',
    pathEn: '/solutions/lead-generation',
    pathEl: '/el/lyseis/dimiourgia-leads',
    descriptionEn: 'Optimize lead capture pages',
    descriptionEl: 'Βελτιστοποίηση σελίδων lead capture',
  },
  competitorComparison: {
    anchorEn: 'Competitor Comparison',
    anchorEl: 'Σύγκριση Ανταγωνιστών',
    pathEn: '/solutions/competitor-comparison',
    pathEl: '/el/lyseis/sygkrisi-antagoniston',
    descriptionEn: 'Compare UX against competitors',
    descriptionEl: 'Σύγκρινε το UX με ανταγωνιστές',
  },
  figmaAnalysis: {
    anchorEn: 'Figma Analysis',
    anchorEl: 'Figma Analysis',
    pathEn: '/contact',
    pathEl: '/el/lyseis/figma-analysi',
    descriptionEn: 'Analyze Figma designs before launch',
    descriptionEl: 'Ανάλυση Figma designs πριν το launch',
  },
  pricing: {
    anchorEn: 'Pricing Plans',
    anchorEl: 'Τιμές & Πλάνα',
    pathEn: '/pricing',
    pathEl: '/el/times',
    descriptionEn: 'View pricing and plans',
    descriptionEl: 'Δες τιμές και πλάνα',
  },
  aiHub: {
    anchorEn: 'AI Hub',
    anchorEl: 'AI Hub',
    pathEn: '/blog',
    pathEl: '/el/ai-hub',
    descriptionEn: 'AI tools and models directory',
    descriptionEl: 'Κατάλογος AI εργαλείων και μοντέλων',
  },
  analyticsHub: {
    anchorEn: 'Analytics Hub',
    anchorEl: 'Analytics Hub',
    pathEn: '/blog',
    pathEl: '/el/analytics-hub',
    descriptionEn: 'AI eye tracking analytics',
    descriptionEl: 'AI eye tracking analytics',
  },
  croTools: {
    anchorEn: 'CRO Tools',
    anchorEl: 'CRO Εργαλεία',
    pathEn: '/what-is-cro',
    pathEl: '/el/cro-tools',
    descriptionEn: 'Essential CRO tools directory',
    descriptionEl: 'Κατάλογος CRO εργαλείων',
  },
  blog: {
    anchorEn: 'Blog',
    anchorEl: 'Blog',
    pathEn: '/blog',
    pathEl: '/el/blog',
    descriptionEn: 'CRO and UX insights',
    descriptionEl: 'CRO και UX insights',
  },
  croAuditGuide: {
    anchorEn: 'CRO audit guide',
    anchorEl: 'CRO audit guide',
    pathEn: '/cro-audit',
    pathEl: '/cro-audit',
    descriptionEn: 'What to check and how to run a CRO audit',
    descriptionEl: 'Τι ελέγχει και πώς γίνεται ένα CRO audit',
  },
  croChecklist: {
    anchorEn: 'CRO checklist',
    anchorEl: 'CRO checklist',
    pathEn: '/cro-checklist',
    pathEl: '/cro-checklist',
    descriptionEn: 'Every point the audit checks, as a list',
    descriptionEl: 'Όσα ελέγχει το audit, σε λίστα',
  },
  shopifySolution: {
    anchorEn: 'Shopify CRO audit',
    anchorEl: 'Shopify CRO audit',
    pathEn: '/shopify-cro-audit',
    pathEl: '/shopify-cro-audit',
    descriptionEn: 'Product, collection and cart pages on Shopify',
    descriptionEl: 'Σελίδες προϊόντος, συλλογής και καλαθιού στο Shopify',
  },
  compareHotjar: {
    anchorEn: 'EyeCaptain vs Hotjar',
    anchorEl: 'EyeCaptain vs Hotjar',
    pathEn: '/compare/eyecaptain-vs-hotjar',
    pathEl: '/compare/eyecaptain-vs-hotjar',
    descriptionEn: 'Predicted attention vs recorded sessions',
    descriptionEl: 'Προβλεπόμενη προσοχή ή καταγραφές επισκέψεων',
  },
  compareAttentionInsight: {
    anchorEn: 'EyeCaptain vs Attention Insight',
    anchorEl: 'EyeCaptain vs Attention Insight',
    pathEn: '/compare/eyecaptain-vs-attention-insight',
    pathEl: '/compare/eyecaptain-vs-attention-insight',
    descriptionEn: 'Two AI attention tools side by side',
    descriptionEl: 'Δύο εργαλεία AI προσοχής δίπλα δίπλα',
  },
  croPageAudit: {
    anchorEn: 'CRO Page Audit',
    anchorEl: 'CRO Page Audit',
    pathEn: '/cro-page-audit',
    pathEl: '/cro-page-audit',
    descriptionEn: 'One audit, judged by the rules of your kind of site',
    descriptionEl: 'Ένα audit με τα κριτήρια του δικού σας τύπου site',
  },
  saasSolution: {
    anchorEn: 'SaaS CRO audit',
    anchorEl: 'CRO audit για SaaS',
    pathEn: '/solutions/saas',
    pathEl: '/solutions/saas',
    descriptionEn: 'Pricing, sign-up and trial pages',
    descriptionEl: 'Σελίδες τιμών, εγγραφής και trial',
  },
  bookingSolution: {
    anchorEn: 'Booking site CRO audit',
    anchorEl: 'CRO audit για κρατήσεις',
    pathEn: '/solutions/booking',
    pathEl: '/solutions/booking',
    descriptionEn: 'Hotels, clinics, restaurants and tours',
    descriptionEl: 'Ξενοδοχεία, κλινικές, εστιατόρια, εκδρομές',
  },
  informationSolution: {
    anchorEn: 'Content site CRO audit',
    anchorEl: 'CRO audit για sites περιεχομένου',
    pathEn: '/solutions/information-sites',
    pathEl: '/solutions/information-sites',
    descriptionEn: 'Blogs, magazines and newsletters',
    descriptionEl: 'Blogs, περιοδικά και newsletters',
  },
  funnelAudit: {
    anchorEn: 'Funnel Audit',
    anchorEl: 'Funnel Audit',
    pathEn: '/solutions/funnel-analysis',
    pathEl: '/solutions/funnel-analysis',
    descriptionEn: 'Find the step where customers drop off',
    descriptionEl: 'Βρείτε σε ποιο βήμα φεύγουν οι πελάτες',
  },
  woocommerceSolution: {
    anchorEn: 'WooCommerce CRO audit',
    anchorEl: 'WooCommerce CRO audit',
    pathEn: '/woocommerce-cro-audit',
    pathEl: '/woocommerce-cro-audit',
    descriptionEn: 'Product, cart and checkout on WooCommerce',
    descriptionEl: 'Προϊόν, καλάθι και checkout στο WooCommerce',
  },
  bigcommerceSolution: {
    anchorEn: 'BigCommerce CRO audit',
    anchorEl: 'BigCommerce CRO audit',
    pathEn: '/bigcommerce-cro-audit',
    pathEl: '/bigcommerce-cro-audit',
    descriptionEn: 'Category, product and checkout pages',
    descriptionEl: 'Κατηγορίες, προϊόντα και checkout',
  },
  magentoSolution: {
    anchorEn: 'Magento CRO audit',
    anchorEl: 'Magento CRO audit',
    pathEn: '/magento-cro-audit',
    pathEl: '/magento-cro-audit',
    descriptionEn: 'Magento Open Source and Adobe Commerce',
    descriptionEl: 'Magento Open Source και Adobe Commerce',
  },
  wixSolution: {
    anchorEn: 'Wix CRO audit',
    anchorEl: 'Wix CRO audit',
    pathEn: '/wix-cro-audit',
    pathEl: '/wix-cro-audit',
    descriptionEn: 'Wix Stores and business sites',
    descriptionEl: 'Wix Stores και επαγγελματικά sites',
  },
  squarespaceSolution: {
    anchorEn: 'Squarespace CRO audit',
    anchorEl: 'Squarespace CRO audit',
    pathEn: '/squarespace-cro-audit',
    pathEl: '/squarespace-cro-audit',
    descriptionEn: 'Stores, studios and service sites',
    descriptionEl: 'Καταστήματα, studios και sites υπηρεσιών',
  },
  wooCheckoutGuide: {
    anchorEn: 'WooCommerce checkout optimization',
    anchorEl: 'WooCommerce checkout optimization',
    pathEn: '/woocommerce-checkout-optimization',
    pathEl: '/woocommerce-checkout-optimization',
    descriptionEn: '14 checkout fixes that cut abandonment',
    descriptionEl: '14 διορθώσεις στο checkout',
  },
  shopifyProductGuide: {
    anchorEn: 'Shopify product page best practices',
    anchorEl: 'Shopify product page best practices',
    pathEn: '/shopify-product-page-best-practices',
    pathEl: '/shopify-product-page-best-practices',
    descriptionEn: '15 checks for more sales',
    descriptionEl: '15 έλεγχοι για περισσότερες πωλήσεις',
  },
  webflowLandingChecklist: {
    anchorEn: 'Webflow landing page checklist',
    anchorEl: 'Webflow landing page checklist',
    pathEn: '/webflow-landing-page-checklist',
    pathEl: '/webflow-landing-page-checklist',
    descriptionEn: '20 points before you publish',
    descriptionEl: '20 σημεία πριν τη δημοσίευση',
  },
  webflowSolution: {
    anchorEn: 'Webflow CRO audit',
    anchorEl: 'Webflow CRO audit',
    pathEn: '/webflow-cro-audit',
    pathEl: '/webflow-cro-audit',
    descriptionEn: 'Landing, SaaS and agency pages',
    descriptionEl: 'Landing, SaaS και σελίδες agency',
  },
  whatIsEyeCaptain: {
    anchorEn: 'What is EyeCaptain',
    anchorEl: 'What is EyeCaptain',
    pathEn: '/what-is-eyecaptain',
    pathEl: '/what-is-eyecaptain',
    descriptionEn: 'What it audits, for whom, and what it costs',
    descriptionEl: 'Τι ελέγχει, για ποιον και πόσο κοστίζει',
  },
  userAgent: {
    anchorEn: 'User Agent',
    anchorEl: 'User Agent',
    pathEn: '/solutions/user-agent',
    pathEl: '/solutions/user-agent',
    descriptionEn: 'An AI visitor tests your site with a goal',
    descriptionEl: 'Ένας AI επισκέπτης δοκιμάζει το site σας',
  },
};

// Per-page link configurations
export const PAGE_LINKS: Record<string, string[]> = {
  // CRO Page Audit family
  'cro-page-audit': ['croAuditGuide', 'funnelAudit', 'userAgent', 'croChecklist'],
  'solutions/saas': ['croPageAudit', 'leadgenSolution', 'funnelAudit', 'croChecklist'],
  'solutions/booking': ['croPageAudit', 'userAgent', 'funnelAudit', 'croChecklist'],
  'solutions/information-sites': ['croPageAudit', 'aiHeatmaps', 'leadgenSolution', 'croChecklist'],
  'solutions/funnel-analysis': ['croPageAudit', 'userAgent', 'ecommerceSolution', 'abTestingGuide'],
  'solutions/user-agent': ['croPageAudit', 'funnelAudit', 'bookingSolution', 'abTestingGuide'],
  // Pillar pages
  'what-is-cro': ['croAuditGuide', 'croChecklist', 'croTechniques', 'aiHeatmaps', 'abTestingGuide', 'freeCroAnalysis'],
  'what-is-ux': ['uxAuditGuide', 'heatmapGuide', 'aiHeatmaps', 'neuromarketing', 'freeWebsiteAudit'],
  'how-it-works': ['aiHeatmaps', 'croTechniques', 'freeCroAnalysis'],
  
  // Feature pages
  'features/ai-heatmaps': ['heatmapGuide', 'liveHeatmaps', 'compareAttentionInsight', 'compareHotjar', 'freeWebsiteAudit'],
  'features/live-heatmaps': ['aiHeatmaps', 'heatmapGuide', 'abTestingFeature', 'compareHotjar'],
  'features/ab-testing': ['abTestingGuide', 'liveHeatmaps', 'visualMockups', 'compareVwo'],
  'features/analytics-integrations': ['croPageAudit', 'aiAgentFeature', 'liveHeatmaps', 'compareClarity'],
  'features/visual-mockups': ['croPageAudit', 'abTestingFeature', 'figmaDesignAudit'],
  'features/ai-agent': ['analyticsIntegrations', 'croPageAudit', 'liveHeatmaps'],
  'features/figma-design-audit': ['croPageAudit', 'visualMockups', 'competitorComparison'],
  'features/neuromarketing': ['croTechniques', 'whatIsCro', 'aiHeatmaps'],
  'features/cro-techniques': ['abTestingGuide', 'neuromarketing', 'freeCroAnalysis'],
  'features/ai-captain': ['aiCro', 'aiToolsGuide'],
  
  // Solution pages
  'solutions/ecommerce': ['croAuditGuide', 'shopifySolution', 'woocommerceSolution', 'ecommerceCroGuide'],
  'shopify-cro-audit': ['shopifyProductGuide', 'ecommerceSolution', 'woocommerceSolution', 'ecommerceCroGuide'],
  // Platform pages (search only)
  'woocommerce-cro-audit': ['wooCheckoutGuide', 'ecommerceSolution', 'shopifySolution', 'ecommerceCroGuide'],
  'bigcommerce-cro-audit': ['ecommerceSolution', 'funnelAudit', 'ecommerceCroGuide', 'magentoSolution'],
  'magento-cro-audit': ['ecommerceSolution', 'bigcommerceSolution', 'ecommerceCroGuide', 'croChecklist'],
  'wix-cro-audit': ['ecommerceSolution', 'leadgenSolution', 'squarespaceSolution', 'croChecklist'],
  'squarespace-cro-audit': ['leadgenSolution', 'ecommerceSolution', 'wixSolution', 'croChecklist'],
  'webflow-cro-audit': ['webflowLandingChecklist', 'leadgenSolution', 'saasSolution', 'croChecklist'],
  'woocommerce-checkout-optimization': ['woocommerceSolution', 'ecommerceSolution', 'ecommerceCroGuide', 'croChecklist'],
  'shopify-product-page-best-practices': ['shopifySolution', 'ecommerceSolution', 'ecommerceCroGuide', 'croChecklist'],
  'webflow-landing-page-checklist': ['webflowSolution', 'leadgenSolution', 'abTestingGuide', 'croChecklist'],
  'what-is-eyecaptain': ['howItWorks', 'ecommerceSolution', 'leadgenSolution', 'pricing'],
  'cro-checklist': ['croAuditGuide', 'freeWebsiteAudit', 'whatIsCro', 'ecommerceCroGuide'],
  'cro-audit': ['croChecklist', 'freeWebsiteAudit', 'ecommerceSolution', 'leadgenSolution'],
  'free-audit': ['croAuditGuide', 'croChecklist', 'howItWorks', 'pricing'],
  compare: ['aiHeatmaps', 'howItWorks', 'pricing', 'freeWebsiteAudit'],
  'solutions/lead-generation': ['croAuditGuide', 'webflowSolution', 'saasSolution', 'bookingSolution'],
  'solutions/competitor-comparison': ['compareHotjar', 'aiHeatmaps', 'whatIsCro'],
  'solutions/figma-analysis': ['uxAuditGuide', 'whatIsUx'],
  'solutions/content-analysis': ['whatIsCro', 'neuromarketing', 'aiCaptain', 'freeCroAnalysis'],
  'solutions/technical-analysis': ['pagespeed', 'uxAuditGuide', 'freeWebsiteAudit', 'whatIsUx'],
  
  // Guide pages
  'ux-audit-guide': ['aiHeatmaps', 'croTechniques', 'freeWebsiteAudit', 'whatIsCro'],
  'heatmap-analysis-guide': ['aiHeatmaps', 'neuromarketing', 'uxAuditGuide', 'freeCroAnalysis'],
  'ai-cro': ['aiHeatmaps', 'abTestingGuide', 'ecommerceCroGuide', 'croTools'],
  'ab-testing-guide': ['abTestingFeature', 'whatIsCro', 'croTechniques', 'heatmapGuide', 'aiCro'],
  'ecommerce-cro-guide': ['aiHeatmaps', 'ecommerceSolution', 'abTestingGuide', 'neuromarketing'],
  'ecommerce-website-guide': ['uxAuditGuide', 'ecommerceCroGuide', 'freeWebsiteAudit'],
  'leadgen-website-guide': ['leadgenSolution', 'freeCroAnalysis', 'heatmapGuide'],
  'ai-tools-guide': ['aiHeatmaps', 'aiHub', 'aiMarketingGuide'],
  'ai-models-guide': ['aiHub', 'aiToolsGuide', 'aiCro'],
  'cro-tools-guide': ['croTechniques', 'aiHeatmaps', 'whatIsCro', 'freeCroAnalysis'],
  'ai-marketing-guide': ['aiToolsGuide', 'aiModelsGuide', 'neuromarketing'],
  'ai-prompting-guide': ['aiToolsGuide', 'aiModelsGuide', 'aiHub'],
  
  // Hub/Resource pages
  'ai-hub': ['aiToolsGuide', 'aiModelsGuide', 'aiMarketingGuide'],
  'analytics-hub': ['aiHeatmaps', 'heatmapGuide', 'freeWebsiteAudit'],
  'cro-tools': ['croToolsGuide', 'whatIsCro', 'freeCroAnalysis'],
  
  // Tool pages
  'free-cro-analysis': ['whatIsCro', 'croTechniques', 'ecommerceCroGuide', 'aiCro'],
  'free-website-audit': ['uxAuditGuide', 'aiHeatmaps', 'pagespeed', 'whatIsUx'],
  
  // Commercial pages
  'index': ['croAuditGuide', 'freeCroAnalysis', 'aiHeatmaps', 'whatIsCro'],
  'analysis': ['aiHeatmaps', 'ecommerceSolution', 'leadgenSolution', 'figmaAnalysis'],
  'benefits': ['croTechniques', 'freeCroAnalysis', 'whatIsCro'],
  'agencies': ['aiHeatmaps', 'competitorComparison', 'freeCroAnalysis'],
  'pricing': ['freeCroAnalysis', 'freeWebsiteAudit', 'howItWorks'],
  'contact': ['freeCroAnalysis', 'freeWebsiteAudit', 'pricing'],
  'faq': ['howItWorks', 'pricing', 'freeCroAnalysis', 'whatIsCro'],
  
  // Blog (generic for article pages)
  'blog-article': ['whatIsCro', 'aiHeatmaps', 'freeCroAnalysis', 'uxAuditGuide'],
};

export default function RelatedResources({ 
  links, 
  titleEn = 'Related Resources', 
  titleEl = 'Σχετικοί Οδηγοί',
  variant = 'grid',
  maxLinks = 4,
}: RelatedResourcesProps) {
  const { language } = useLanguage();
  const displayLinks = links.slice(0, maxLinks);

  if (variant === 'compact') {
    return (
      <div className="flex flex-wrap gap-2">
        {displayLinks.map((link, i) => (
          <Link
            key={i}
            to={language === 'el' ? link.pathEl : link.pathEn}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
          >
            {language === 'el' ? link.anchorEl : link.anchorEn}

          </Link>
        ))}
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className="border-t border-border/50 pt-6 mt-8">
        <p className="text-sm font-medium text-muted-foreground mb-3">
          {language === 'el' ? titleEl : titleEn}:
        </p>
        <div className="flex flex-wrap gap-3">
          {displayLinks.map((link, i) => (
            <Link
              key={i}
              to={language === 'el' ? link.pathEl : link.pathEn}
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
            >

              {language === 'el' ? link.anchorEl : link.anchorEn}
            </Link>
          ))}
        </div>
      </div>
    );
  }

  // Grid variant (default)
  return (
    <section className="py-10 md:py-16 border-t border-border/30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-2 mb-8">
          <BookOpen className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-bold text-foreground">
            {language === 'el' ? titleEl : titleEn}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {displayLinks.map((link, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
            >
              <Link
                to={language === 'el' ? link.pathEl : link.pathEn}
                className="group block p-4 rounded-xl border border-border/50 bg-card/50 hover:bg-card hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all duration-200"
              >
                <h3 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors mb-1">
                  {language === 'el' ? link.anchorEl : link.anchorEn}
                </h3>
                {(language === 'el' ? link.descriptionEl : link.descriptionEn) && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {language === 'el' ? link.descriptionEl : link.descriptionEn}
                  </p>
                )}
                <span className="inline-flex items-center gap-1 text-xs text-primary mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  {language === 'el' ? 'Διάβασε' : 'Read more'}

                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Helper: get RelatedResources links for a page by its route key */
/** Pages that still exist after the 2026-09 site cleanup; everything else redirects and must not be linked. */
const LIVE_LINK_KEYS = new Set([
  'aiHeatmaps', 'neuromarketing', 'croTechniques', 'whatIsCro', 'howItWorks', 'heatmapGuide', 'abTestingGuide', 'ecommerceCroGuide',
  'freeWebsiteAudit', 'ecommerceSolution', 'leadgenSolution', 'competitorComparison', 'pricing', 'blog',
  'shopifySolution', 'croChecklist', 'compareHotjar', 'compareAttentionInsight', 'compareClarity', 'compareVwo',
  'croPageAudit', 'saasSolution', 'bookingSolution', 'informationSolution', 'funnelAudit', 'userAgent',
  'woocommerceSolution', 'bigcommerceSolution', 'magentoSolution', 'wixSolution', 'squarespaceSolution', 'webflowSolution',
  'whatIsEyeCaptain',
  'liveHeatmaps', 'abTestingFeature', 'analyticsIntegrations', 'visualMockups', 'aiAgentFeature', 'figmaDesignAudit',
]);
const KEY_ALIASES: Record<string, string> = { freeCroAnalysis: 'freeWebsiteAudit', uxAuditGuide: 'freeWebsiteAudit', whatIsUx: 'whatIsCro', aiCro: 'howItWorks', pagespeed: 'ecommerceSolution', ecommerceWebsiteGuide: 'ecommerceCroGuide', leadgenWebsiteGuide: 'leadgenSolution', croTools: 'whatIsCro', croToolsGuide: 'whatIsCro' };

export function getPageLinks(pageKey: string): InternalLink[] {
  const linkKeys = PAGE_LINKS[pageKey] || [];
  const seen = new Set<string>();
  return linkKeys
    .map((key) => KEY_ALIASES[key] || key)
    .filter((key) => LIVE_LINK_KEYS.has(key) && !seen.has(key) && seen.add(key))
    .map((key) => INTERNAL_LINKS[key])
    .filter(Boolean);
}
